import React from "react";
import {
  Bug,
  Sparkles,
  Wrench,
  FlaskConical,
  Eye,
  FileText,
  Server,
  Search,
  PenTool,
  PenLine,
  BarChart3,
  Megaphone,
} from "lucide-react";

// ─── Agent Type Avatar ──────────────────
// Rounded square (4px radius) with type icon + colored bg at 15%
// Active state: animated pulsing ring in brand color (2s interval)
export type AgentType =
  | "bug-fixer"
  | "feature-builder"
  | "refactorer"
  | "test-writer"
  | "reviewer"
  | "docs-writer"
  | "devops"
  // Content agents
  | "researcher"
  | "writer"
  | "editor"
  | "seo"
  | "distributor";

interface AgentTypeConfig {
  color: string;
  bgColor: string;
  icon: React.ReactNode;
  label: string;
}

export const AGENT_TYPE_CONFIG: Record<AgentType, AgentTypeConfig> = {
  "bug-fixer": {
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.15)",
    icon: <Bug size={14} strokeWidth={1.5} />,
    label: "Bug Fixer",
  },
  "feature-builder": {
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.15)",
    icon: <Sparkles size={14} strokeWidth={1.5} />,
    label: "Feature Builder",
  },
  refactorer: {
    color: "#3b82f6",
    bgColor: "rgba(59, 130, 246, 0.15)",
    icon: <Wrench size={14} strokeWidth={1.5} />,
    label: "Refactorer",
  },
  "test-writer": {
    color: "#8b5cf6",
    bgColor: "rgba(139, 92, 246, 0.15)",
    icon: <FlaskConical size={14} strokeWidth={1.5} />,
    label: "Test Writer",
  },
  reviewer: {
    color: "#06b6d4",
    bgColor: "rgba(6, 182, 212, 0.15)",
    icon: <Eye size={14} strokeWidth={1.5} />,
    label: "Reviewer",
  },
  "docs-writer": {
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.15)",
    icon: <FileText size={14} strokeWidth={1.5} />,
    label: "Docs Writer",
  },
  devops: {
    color: "#64748b",
    bgColor: "rgba(100, 116, 139, 0.15)",
    icon: <Server size={14} strokeWidth={1.5} />,
    label: "DevOps",
  },
  researcher: {
    color: "#8b5cf6",
    bgColor: "rgba(139, 92, 246, 0.15)",
    icon: <Search size={14} strokeWidth={1.5} />,
    label: "Researcher",
  },
  writer: {
    color: "#a855f7",
    bgColor: "rgba(168, 85, 247, 0.15)",
    icon: <PenTool size={14} strokeWidth={1.5} />,
    label: "Writer",
  },
  editor: {
    color: "#ec4899",
    bgColor: "rgba(236, 72, 153, 0.15)",
    icon: <PenLine size={14} strokeWidth={1.5} />,
    label: "Editor",
  },
  seo: {
    color: "#06b6d4",
    bgColor: "rgba(6, 182, 212, 0.15)",
    icon: <BarChart3 size={14} strokeWidth={1.5} />,
    label: "SEO Specialist",
  },
  distributor: {
    color: "#f97316",
    bgColor: "rgba(249, 115, 22, 0.15)",
    icon: <Megaphone size={14} strokeWidth={1.5} />,
    label: "Distributor",
  },
};

export function AgentTypeAvatar({
  type,
  active = false,
  size = 24,
  className = "",
}: {
  type: AgentType;
  active?: boolean;
  size?: number;
  className?: string;
}) {
  const config = AGENT_TYPE_CONFIG[type];
  const iconSize = Math.round(size * 0.58);

  return (
    <div
      className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        backgroundColor: config.bgColor,
        color: config.color,
        animation: active ? "agentPulse 2s ease-in-out infinite" : undefined,
      }}
      role="img"
      aria-label={config.label}
      title={config.label}
    >
      <span style={{ display: "inline-flex" }} aria-hidden="true">
        {React.cloneElement(config.icon as React.ReactElement, {
          size: iconSize,
        })}
      </span>
    </div>
  );
}

// ─── Helpers to map from issue type strings ──────────────────
const ISSUE_TO_AGENT: Partial<Record<string, AgentType>> = {
  bug: "bug-fixer",
  feature: "feature-builder",
  refactor: "refactorer",
  hotfix: "bug-fixer",
  // Backend content pipeline role names
  content_researcher: "researcher",
  content_outliner: "researcher",
  content_writer_raw: "writer",
  content_editor_voice: "editor",
  seo_optimizer: "seo",
  content_editor_final: "editor",
  social_repurposer: "distributor",
};

export function mapIssueToAgentType(issueType: string): AgentType {
  return ISSUE_TO_AGENT[issueType] ?? (issueType in AGENT_TYPE_CONFIG ? issueType as AgentType : "feature-builder");
}
